import { friendlyDbError } from '../utils/friendlyDbError';
/**
 * Manual Order Store
 * Manages the creation and editing of manual orders
 * Handles draft state, item management, and availability checking
 */

import { create } from 'zustand';
import { supabase } from '../supabase';
import { ManualOrderService } from '../services/manualOrderService';
import type {
  ManualOrder,
  DraftOrder,
  OrderItemInput,
  ProductAvailability,
  OrderAddress,
  ManualOrderStatus
} from '../types/orders';

interface ManualOrderStore {
  // Current order being edited
  currentOrder: ManualOrder | null;
  loading: boolean;
  error: string | null;

  // Draft order (for creation)
  draftOrder: DraftOrder | null;
  isDraftDirty: boolean;

  // Availability check results
  availabilityResults: ProductAvailability[];
  checkingAvailability: boolean;

  // CRUD Actions
  fetchOrderById: (orderId: string, organizationId: string) => Promise<void>;
  createOrder: (organizationId: string, userId: string) => Promise<string | null>;
  updateOrder: (orderId: string, updates: Partial<ManualOrder>, organizationId: string, userId: string) => Promise<boolean>;
  updateOrderItems: (orderId: string, items: OrderItemInput[], organizationId: string, userId: string) => Promise<boolean>;
  refreshOrderFromDeal: (orderId: string, organizationId: string, userId: string) => Promise<boolean>;
  deleteOrder: (orderId: string, organizationId: string, userId: string) => Promise<boolean>;

  // Status Actions
  confirmOrder: (orderId: string, organizationId: string, userId: string) => Promise<boolean>;
  cancelOrder: (orderId: string, reason: string, organizationId: string, userId: string) => Promise<boolean>;
  updateStatus: (orderId: string, status: ManualOrderStatus, organizationId: string, userId: string) => Promise<boolean>;

  // Draft Management
  initEmptyDraft: (clientId?: string) => void;
  initDraftFromOrder: (orderId: string, organizationId: string) => Promise<void>;
  updateDraft: (updates: Partial<DraftOrder>) => void;
  clearDraft: () => void;

  // Draft Item Management
  addItem: (item: OrderItemInput) => void;
  updateItem: (index: number, updates: Partial<OrderItemInput>) => void;
  removeItem: (index: number) => void;
  setItems: (items: OrderItemInput[]) => void;

  // Address Management
  setBillingAddress: (address: OrderAddress) => void;
  setShippingAddress: (address: OrderAddress) => void;
  copyBillingToShipping: () => void;

  // Availability
  checkAvailability: (organizationId: string) => Promise<void>;
  clearAvailability: () => void;

  // Reset
  reset: () => void;
  resetDraft: () => void;
}

const emptyDraft: DraftOrder = {
  client_id: undefined,
  deal_id: undefined,
  price_list_id: undefined,
  billing_address: undefined,
  shipping_address: undefined,
  shipping_method: undefined,
  shipping_cost: 0,
  customer_notes: undefined,
  internal_notes: undefined,
  items: [],
  subtotal: 0,
  discount_total: 0,
  tax_total: 0,
  total: 0
};

const initialState = {
  currentOrder: null,
  loading: false,
  error: null,
  draftOrder: null,
  isDraftDirty: false,
  availabilityResults: [],
  checkingAvailability: false
};

// Helper to calculate totals
const calculateTotals = (items: OrderItemInput[], shippingCost: number = 0) => {
  const subtotal = items.reduce((sum, item) => {
    const lineTotal = (item.unit_price * item.quantity) - (item.discount_amount || 0);
    return sum + lineTotal;
  }, 0);

  const discountTotal = items.reduce((sum, item) => sum + (item.discount_amount || 0), 0);

  const taxTotal = items.reduce((sum, item) => {
    const lineTotal = (item.unit_price * item.quantity) - (item.discount_amount || 0);
    return sum + (lineTotal * (item.tax_rate || 0) / 100);
  }, 0);

  const total = subtotal + taxTotal + shippingCost;

  return { subtotal, discount_total: discountTotal, tax_total: taxTotal, total };
};

export const useManualOrderStore = create<ManualOrderStore>((set, get) => ({
  ...initialState,

  // =====================================================
  // CRUD ACTIONS
  // =====================================================

  fetchOrderById: async (orderId: string, organizationId: string) => {
    try {
      set({ loading: true, error: null });

      const order = await ManualOrderService.getOrderById(orderId, organizationId);

      set({
        currentOrder: order,
        loading: false,
        error: null
      });

    } catch (error) {
      console.error('Error fetching manual order:', error);
      set({
        error: friendlyDbError(error) || 'Errore caricamento ordine',
        loading: false
      });
    }
  },

  createOrder: async (organizationId: string, userId: string) => {
    const { draftOrder, loading } = get();
    if (loading) return null;

    if (!draftOrder) {
      set({ error: 'Nessun ordine da creare' });
      return null;
    }

    if (!draftOrder.client_id) {
      set({ error: 'Seleziona un cliente' });
      return null;
    }

    if (draftOrder.items.length === 0) {
      set({ error: 'Aggiungi almeno un prodotto' });
      return null;
    }

    try {
      set({ loading: true, error: null });

      const requestId = draftOrder.request_id || crypto.randomUUID();
      set({ draftOrder: { ...draftOrder, request_id: requestId } });
      const order = await ManualOrderService.createOrder({
        request_id: requestId,
        client_id: draftOrder.client_id,
        deal_id: draftOrder.deal_id,
        price_list_id: draftOrder.price_list_id,
        billing_address: draftOrder.billing_address,
        shipping_address: draftOrder.shipping_address,
        shipping_location_id: draftOrder.shipping_location_id ?? null,
        shipping_method: draftOrder.shipping_method,
        shipping_cost: draftOrder.shipping_cost,
        customer_notes: draftOrder.customer_notes,
        internal_notes: draftOrder.internal_notes,
        items: draftOrder.items,
        auto_confirm: false // Start as draft
      }, {
        organizationId,
        userId
      });

      set({
        currentOrder: order,
        loading: false,
        draftOrder: null,
        isDraftDirty: false
      });

      return order.id;

    } catch (error) {
      console.error('Error creating order:', error);
      set({
        error: friendlyDbError(error) || 'Errore creazione ordine',
        loading: false
      });
      return null;
    }
  },

  updateOrder: async (orderId: string, updates: Partial<ManualOrder>, organizationId: string, userId: string) => {
    try {
      set({ loading: true, error: null });

      await ManualOrderService.updateOrder(orderId, updates, {
        organizationId,
        userId
      });

      // Refresh order
      await get().fetchOrderById(orderId, organizationId);

      set({ loading: false });
      return true;

    } catch (error) {
      console.error('Error updating order:', error);
      set({
        error: friendlyDbError(error) || 'Errore aggiornamento ordine',
        loading: false
      });
      return false;
    }
  },

  deleteOrder: async (orderId: string, organizationId: string, userId: string) => {
    try {
      set({ loading: true, error: null });

      await ManualOrderService.deleteOrder(orderId, {
        organizationId,
        userId
      });

      set({
        currentOrder: null,
        loading: false
      });

      return true;

    } catch (error) {
      console.error('Error deleting order:', error);
      set({
        error: friendlyDbError(error) || 'Errore eliminazione ordine',
        loading: false
      });
      return false;
    }
  },

  updateOrderItems: async (orderId: string, items: OrderItemInput[], organizationId: string, userId: string) => {
    try {
      set({ loading: true, error: null });

      const order = await ManualOrderService.updateOrderItems(orderId, items, {
        organizationId,
        userId
      });

      set({
        currentOrder: order,
        loading: false
      });

      return true;

    } catch (error) {
      console.error('Error updating order items:', error);
      set({
        error: friendlyDbError(error) || 'Errore aggiornamento prodotti',
        loading: false
      });
      return false;
    }
  },

  refreshOrderFromDeal: async (orderId: string, organizationId: string, userId: string) => {
    try {
      set({ loading: true, error: null });
      const order = await ManualOrderService.refreshOrderFromDeal(orderId, { organizationId, userId });
      set({ currentOrder: order, loading: false });
      return true;
    } catch (error) {
      console.error('Error refreshing order from deal:', error);
      set({
        error: friendlyDbError(error) || "Errore aggiornamento dall'offerta",
        loading: false
      });
      return false;
    }
  },

  // =====================================================
  // STATUS ACTIONS
  // =====================================================

  confirmOrder: async (orderId: string, organizationId: string, userId: string) => {
    try {
      set({ loading: true, error: null });

      const order = await ManualOrderService.confirmOrder(orderId, {
        organizationId,
        userId
      });

      set({
        currentOrder: order,
        loading: false
      });

      return true;

    } catch (error) {
      console.error('Error confirming order:', error);
      set({
        error: friendlyDbError(error) || 'Errore conferma ordine',
        loading: false
      });
      return false;
    }
  },

  cancelOrder: async (orderId: string, reason: string, organizationId: string, userId: string) => {
    try {
      set({ loading: true, error: null });

      const order = await ManualOrderService.cancelOrder(orderId, reason, {
        organizationId,
        userId
      });

      set({
        currentOrder: order,
        loading: false
      });

      return true;

    } catch (error) {
      console.error('Error cancelling order:', error);
      set({
        error: friendlyDbError(error) || 'Errore annullamento ordine',
        loading: false
      });
      return false;
    }
  },

  updateStatus: async (orderId: string, status: ManualOrderStatus, organizationId: string, userId: string) => {
    try {
      set({ loading: true, error: null });

      const order = await ManualOrderService.updateOrderStatus(orderId, status, {
        organizationId,
        userId
      });

      set({
        currentOrder: order,
        loading: false
      });

      return true;

    } catch (error) {
      console.error('Error updating order status:', error);
      set({
        error: friendlyDbError(error) || 'Errore aggiornamento stato',
        loading: false
      });
      return false;
    }
  },

  // =====================================================
  // DRAFT MANAGEMENT
  // =====================================================

  initEmptyDraft: (clientId?: string) => {
    set({
      draftOrder: {
        ...emptyDraft,
        client_id: clientId
      },
      isDraftDirty: false,
      availabilityResults: []
    });
  },

  initDraftFromOrder: async (orderId: string, organizationId: string) => {
    try {
      set({ loading: true, error: null });

      const order = await ManualOrderService.getOrderById(orderId, organizationId);

      // Convert order items to draft items
      const items: OrderItemInput[] = (order.items || []).map(item => ({
        product_id: item.product_id,
        variation_id: item.variation_id,
        product_name: item.product_name,
        product_sku: item.product_sku,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount_amount: item.discount_amount,
        tax_rate: item.tax_rate,
        notes: item.notes,
        line_type: item.line_type === 'custom' ? 'custom' : undefined
      }));

      const totals = calculateTotals(items, order.shipping_cost);

      set({
        draftOrder: {
          // deal_id NON viene ripreso: un ordine ricalcato su un altro e' un
          // ordine nuovo, non una seconda evasione della stessa offerta (le
          // righe dell'offerta risulterebbero ordinate due volte).
          client_id: order.client_id,
          price_list_id: order.price_list_id,
          billing_address: order.billing_address,
          shipping_address: order.shipping_address,
          shipping_location_id: order.shipping_location_id ?? null,
          shipping_method: order.shipping_method,
          shipping_cost: order.shipping_cost,
          customer_notes: order.customer_notes,
          internal_notes: order.internal_notes,
          items,
          ...totals
        },
        isDraftDirty: false,
        loading: false
      });

    } catch (error) {
      console.error('Error loading order for draft:', error);
      set({
        error: friendlyDbError(error) || 'Errore caricamento ordine',
        loading: false
      });
    }
  },

  updateDraft: (updates: Partial<DraftOrder>) => {
    const { draftOrder } = get();
    if (!draftOrder) return;

    const newDraft = { ...draftOrder, ...updates };

    // Recalculate totals if items or shipping changed
    if (updates.items !== undefined || updates.shipping_cost !== undefined) {
      const totals = calculateTotals(newDraft.items, newDraft.shipping_cost);
      Object.assign(newDraft, totals);
    }

    set({
      draftOrder: newDraft,
      isDraftDirty: true
    });
  },

  clearDraft: () => {
    set({
      draftOrder: null,
      isDraftDirty: false,
      availabilityResults: []
    });
  },

  // =====================================================
  // ITEM MANAGEMENT
  // =====================================================

  addItem: (item: OrderItemInput) => {
    const { draftOrder } = get();
    if (!draftOrder) return;

    const newItems = [...draftOrder.items, item];
    const totals = calculateTotals(newItems, draftOrder.shipping_cost);

    set({
      draftOrder: { ...draftOrder, items: newItems, ...totals },
      isDraftDirty: true
    });
  },

  updateItem: (index: number, updates: Partial<OrderItemInput>) => {
    const { draftOrder } = get();
    if (!draftOrder || index < 0 || index >= draftOrder.items.length) return;

    const newItems = [...draftOrder.items];
    newItems[index] = { ...newItems[index], ...updates };
    const totals = calculateTotals(newItems, draftOrder.shipping_cost);

    set({
      draftOrder: { ...draftOrder, items: newItems, ...totals },
      isDraftDirty: true
    });
  },

  removeItem: (index: number) => {
    const { draftOrder } = get();
    if (!draftOrder || index < 0 || index >= draftOrder.items.length) return;

    const newItems = draftOrder.items.filter((_, i) => i !== index);
    const totals = calculateTotals(newItems, draftOrder.shipping_cost);

    set({
      draftOrder: { ...draftOrder, items: newItems, ...totals },
      isDraftDirty: true
    });
  },

  setItems: (items: OrderItemInput[]) => {
    const { draftOrder } = get();
    if (!draftOrder) return;

    const totals = calculateTotals(items, draftOrder.shipping_cost);

    set({
      draftOrder: { ...draftOrder, items, ...totals },
      isDraftDirty: true
    });
  },

  // =====================================================
  // ADDRESS MANAGEMENT
  // =====================================================

  setBillingAddress: (address: OrderAddress) => {
    const { draftOrder } = get();
    if (!draftOrder) return;

    set({
      draftOrder: { ...draftOrder, billing_address: address },
      isDraftDirty: true
    });
  },

  setShippingAddress: (address: OrderAddress) => {
    const { draftOrder } = get();
    if (!draftOrder) return;

    set({
      draftOrder: { ...draftOrder, shipping_address: address },
      isDraftDirty: true
    });
  },

  copyBillingToShipping: () => {
    const { draftOrder } = get();
    if (!draftOrder || !draftOrder.billing_address) return;

    set({
      draftOrder: { ...draftOrder, shipping_address: { ...draftOrder.billing_address } },
      isDraftDirty: true
    });
  },

  // =====================================================
  // AVAILABILITY CHECK
  // =====================================================

  checkAvailability: async (organizationId: string) => {
    const { draftOrder } = get();
    if (!draftOrder || draftOrder.items.length === 0) return;

    try {
      set({ checkingAvailability: true });

      const results = await ManualOrderService.checkItemsAvailability(
        draftOrder.items,
        organizationId
      );

      set({
        availabilityResults: results,
        checkingAvailability: false
      });

    } catch (error) {
      console.error('Error checking availability:', error);
      set({ checkingAvailability: false });
    }
  },

  clearAvailability: () => {
    set({ availabilityResults: [] });
  },

  // =====================================================
  // RESET
  // =====================================================

  reset: () => {
    set(initialState);
  },

  resetDraft: () => {
    set({
      draftOrder: null,
      isDraftDirty: false,
      availabilityResults: []
    });
  }
}));

// =====================================================
// HELPER HOOKS
// =====================================================

/**
 * Get draft totals
 */
export const useDraftTotals = () => {
  const draftOrder = useManualOrderStore(state => state.draftOrder);

  if (!draftOrder) {
    return { subtotal: 0, discount_total: 0, tax_total: 0, shipping_cost: 0, total: 0 };
  }

  return {
    subtotal: draftOrder.subtotal,
    discount_total: draftOrder.discount_total,
    tax_total: draftOrder.tax_total,
    shipping_cost: draftOrder.shipping_cost,
    total: draftOrder.total
  };
};

/**
 * Check if all items are available
 */
export const useAllItemsAvailable = () => {
  const results = useManualOrderStore(state => state.availabilityResults);

  if (results.length === 0) return true;
  return results.every(r => r.is_available);
};
